/**
 * gui-react's `AclActor` (Java's actor JSON) as `@brew/ui/actors`' neutral
 * `Actor`, for the `ActorSearch` and `ActorAvatar` wrappers. One mapping, so
 * the picker rows and every avatar agree on kind, label and image.
 */
import { isGroupRef, parseActorRef, type Actor, type RenderActorImage } from '@brew/ui/actors';
import type { AclActor } from '@/api/acl';
import ApiImage from '@/components/common/ApiImage';
import { groupAvatarPath, groupAvatarSrc } from '@/api/brewauthGroups';
import { getAvatarUrl } from '@/api/profiles';
import { getLabel } from '@/utils/label';

/** A group by its Java `$type`, else by its ref (any case spelling of the scheme). */
export function isGroupActor(a: AclActor): boolean {
  if (a.$type === 'group') return true;
  return typeof a.$reference === 'string' && isGroupRef(a.$reference);
}

/** The label every ACL screen shows: the metadata label, else the id. */
export function aclActorLabel(a: AclActor): string {
  return getLabel(a.metadata?.label) || a._id || '';
}

/**
 * The avatar URL rule gui-react uses:
 *  1. `$icon` when the actor carries one (Java's actor JSON always does);
 *  2. the single-group shape (`toAclActor` of a brewauth `GroupView`; Java's
 *     retired `GET /api/groups/:id` carried the same key) has an `avatar` key
 *     instead: the group path when it is set (versioned by the avatar id when
 *     it is one, #115, so a replaced avatar is refetched), nothing when it is
 *     not (known to have none);
 *  3. otherwise the kind's avatar path.
 */
export function aclActorAvatarUrl(a: AclActor): string | undefined {
  if (a.$icon) return a.$icon;
  if (Object.hasOwn(a, 'avatar')) {
    if (!a.avatar || !a._id) return undefined;
    // A document id versions the URL; a bare `true` (the older shape) cannot.
    return typeof a.avatar === 'string' ? groupAvatarSrc(a._id, a.avatar) : groupAvatarPath(a._id);
  }
  if (!a._id) return undefined;
  return isGroupActor(a) ? groupAvatarPath(a._id) : getAvatarUrl(a._id);
}

/**
 * The picker row's sublabel: the id's first 8 characters, to tell two
 * same-named actors apart. The kind is NOT repeated here ("user · …" today):
 * the picker's own tag already says person or group.
 */
function shortId(a: AclActor): string | undefined {
  const id = parseActorRef(a.$reference)?.id ?? a._id;
  return id ? id.slice(0, 8) : undefined;
}

export function aclActorToActor(a: AclActor): Actor {
  const group = isGroupActor(a);
  return {
    ref: a.$reference,
    kind: group ? 'group' : 'profile',
    id: parseActorRef(a.$reference)?.id ?? a._id,
    label: aclActorLabel(a),
    sublabel: shortId(a),
    avatarUrl: aclActorAvatarUrl(a),
    isSystem: !!a.$isSystem,
  };
}

/**
 * The avatar image through `ApiImage` (the authenticated api client,
 * jorigin/brewauth#96). `ApiImage` renders nothing while loading and reports
 * a failed or blank avatar only through `onError`, so passing `onError` on is
 * what lets the shared avatar fall back to initials.
 */
export const renderApiImage: RenderActorImage = (src, alt, onError) => (
  <ApiImage src={src} alt={alt} onError={onError} loading="lazy" />
);
